import { useEffect, useRef } from 'react';
import type { LegalTemplate, LegalTemplateVersion } from '@/mocks/superAdminLegalTemplates';
import { getVersionColor, getVersionLabel } from '@/mocks/superAdminLegalTemplates';

interface TemplateHistoryModalProps {
  template: LegalTemplate | null;
  onClose: () => void;
  onActivateVersion: (templateId: number, versionNumber: number) => void;
  onArchiveVersion: (templateId: number, versionNumber: number) => void;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function TemplateHistoryModal({
  template,
  onClose,
  onActivateVersion,
  onArchiveVersion,
}: TemplateHistoryModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  if (!template) return null;

  const sortedVersions = [...template.versions].sort((a, b) => b.versionNumber - a.versionNumber);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[75] flex items-start justify-center p-4 pt-10 pb-10 bg-black/60 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="relative w-full max-w-[700px] bg-background-50 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center shrink-0">
              <i className={`${template.typeIcon} text-foreground-500 text-base`}></i>
            </div>
            <div className="min-w-0">
              <p className="text-foreground-800 text-xs font-semibold truncate">{template.typeLabel}</p>
              <p className="text-foreground-400 text-[10px]">
                Historial de versiones · {template.versions.length} {template.versions.length === 1 ? 'versión' : 'versiones'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Timeline */}
        <div className="p-5 max-h-[65vh] overflow-y-auto">
          <div className="relative pl-8 space-y-0">
            {/* Vertical line */}
            <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-background-200"></div>

            {sortedVersions.map((version, idx) => {
              const isLatest = idx === 0;
              const isActive = version.status === 'active';

              return (
                <div key={version.versionNumber} className="relative pb-6 last:pb-0">
                  {/* Timeline dot */}
                  <div
                    className={`absolute left-[-23px] top-1.5 w-4 h-4 rounded-full border-2 ${
                      isActive
                        ? 'bg-emerald-400 border-emerald-400 ring-4 ring-emerald-50'
                        : version.status === 'draft'
                          ? 'bg-amber-400 border-amber-400'
                          : 'bg-stone-300 border-stone-300'
                    }`}
                  ></div>

                  {/* Version card */}
                  <div
                    className={`rounded-xl border p-4 ${
                      isActive
                        ? 'border-emerald-200 bg-emerald-50/40'
                        : version.status === 'draft'
                          ? 'border-amber-200 bg-amber-50/30'
                          : 'border-background-200 bg-background-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-foreground-800 text-xs font-bold">
                            v{version.versionNumber}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold border ${getVersionColor(version.status)}`}>
                            {getVersionLabel(version.status)}
                          </span>
                          {isLatest && (
                            <span className="text-foreground-300 text-[9px] font-medium">· Actual</span>
                          )}
                        </div>
                        <p className="text-foreground-700 text-[11px] font-semibold mt-1">{version.title}</p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        {version.status === 'draft' && (
                          <button
                            onClick={() => onActivateVersion(template.id, version.versionNumber)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-600 text-[9px] font-semibold hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-check-line text-[9px]"></i>
                            Activar
                          </button>
                        )}
                        {version.status === 'active' && sortedVersions.length > 1 && (
                          <button
                            onClick={() => onArchiveVersion(template.id, version.versionNumber)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-stone-50 border border-stone-200 text-stone-500 text-[9px] font-semibold hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-archive-line text-[9px]"></i>
                            Archivar
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Content snippet */}
                    <p className="text-foreground-400 text-[10px] leading-relaxed line-clamp-2 mb-2">
                      {version.content.slice(0, 150).replace(/\n/g, ' ')}...
                    </p>

                    {/* Meta */}
                    <div className="flex items-center gap-3 text-foreground-300 text-[9px]">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line text-[8px]"></i>
                        {version.createdBy}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line text-[8px]"></i>
                        {formatDate(version.createdAt)}
                      </span>
                      {version.notes && (
                        <span className="flex items-center gap-1 text-foreground-400">
                          <i className="ri-chat-quote-line text-[8px]"></i>
                          {version.notes}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-background-200">
          <p className="text-foreground-300 text-[10px]">
            La versión activa es la que se aplica a todos los contratos nuevos
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar Historial
          </button>
        </div>
      </div>
    </div>
  );
}